// <purpose>THE de-DE one-decimal number face — „10,7", „1.024,0" — shared by every surface that
// prints a measured quantity with one decimal (the descriptor pills' seconds, the attachment
// sizes). One helper, so the locale and the decimal rule can never drift between two copies.</purpose>
export const formatDeOneDecimal = (n: number): string =>
  n.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/**
 * <purpose>Unified German date/time formatting for all ticket list views.</purpose>
 * <constraints>'short' omits time (tooltips provide full date). 'long' includes HH:MM.</constraints>
 */

export const formatRelativeTime = (dateStr: string, style: 'short' | 'long' = 'short'): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.floor((today.getTime() - dateDay.getTime()) / 86400000);

  if (style === 'long') {
    if (diffMin < 1) return 'gerade eben';
    if (diffMin < 60) return `vor ${diffMin} Min.`;
    const hhmm = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    if (diffDays === 0) return `heute, ${hhmm}`;
    if (diffDays === 1) return `gestern, ${hhmm}`;
    if (diffDays < 7) return `${date.toLocaleDateString('de-DE', { weekday: 'long' })}, ${hhmm}`;
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
  }

  // 'short' — compact relative label, no time
  if (diffMin < 1) return 'gerade';
  if (diffMin < 60) return `${diffMin} Min.`;
  if (diffDays === 0) return 'heute';
  if (diffDays === 1) return 'gestern';
  if (diffDays < 7) return ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'][date.getDay()];
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

export const formatAbsoluteDate = (dateStr: string): string =>
  new Date(dateStr).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

export type AgeClock = 'received' | 'upload';
// `compact` is the list row's one age face („3 Tg.", part 7 fix loop, 2026-10-12, operator ruling);
// the long „seit 3 Tagen" form was deleted with its last renderer.
export interface AgeText { compact: string; clock: AgeClock; stampIso: string }

const HOUR_MS = 3600000;
const DAY_MS = 24 * HOUR_MS;
const FUTURE_TOLERANCE_MS = 5 * 60000;
const IMPLAUSIBLE_GAP_MS = 365 * DAY_MS;

// The Europe/Berlin calendar day a stamp falls on, as a whole-day index (days since the epoch of
// that LOCAL date). The product's clerks read "heute" and "1 Tg." by the German calendar,
// whatever zone the browser or the test runner happens to be in.
const BERLIN_DAY = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit',
});
const berlinDayIndex = (ms: number): number => {
  const parts = BERLIN_DAY.formatToParts(new Date(ms));
  const part = (type: string) => Number(parts.find(p => p.type === type)?.value);
  return Math.round(Date.UTC(part('year'), part('month') - 1, part('day')) / DAY_MS);
};

/**
 * <purpose>How long a ticket has been waiting, worded compactly for a list row („3 Tg.").</purpose>
 * <constraints>Reads the customer's clock (receivedAt) unless it is missing, in the future (more
 * than 5 minutes ahead of now) or more than a year before our own createdAt; then it falls back to
 * createdAt and says so via `clock: 'upload'` so the row can label the stamp honestly. A row with
 * NO createdAt reads its arrival stamp for both clocks — and when that arrival is missing or in the
 * future, the upload fallback is NOW, never the customer's future stamp.
 * Days count Europe/Berlin CALENDAR days, not rolling 24 h: a mail from yesterday 17:00 is
 * „1 Tg." at 09:00 today, and anything from today is „heute". `danger` words its first 24 ELAPSED
 * hours in minutes („N Min.", floored at 1) and hours („N Std."); weeks run to 8 weeks, then months
 * (30-day): „N Wo.", „N Mon.". The only age form since part 7 fix loop, 2026-10-12.</constraints>
 */
export const ageText = (
  receivedAtIso: string | null | undefined,
  createdAtIso: string | null | undefined,
  now: Date = new Date(),
  danger = false,
): AgeText => {
  const received = receivedAtIso ? new Date(receivedAtIso).getTime() : NaN;
  const notFuture = Number.isFinite(received) && received <= now.getTime() + FUTURE_TOLERANCE_MS;
  const uploadIso = createdAtIso ?? (notFuture ? (receivedAtIso as string) : now.toISOString());
  const created = new Date(uploadIso).getTime();
  const plausible = notFuture && created - received <= IMPLAUSIBLE_GAP_MS;
  const clock: AgeClock = plausible ? 'received' : 'upload';
  const stampIso = plausible ? (receivedAtIso as string) : uploadIso;
  const stamp = plausible ? received : created;
  const elapsed = now.getTime() - stamp;
  const days = Math.max(0, berlinDayIndex(now.getTime()) - berlinDayIndex(stamp));

  let compact: string;
  if (danger && elapsed < DAY_MS) {
    // A danger mail's first day is ELAPSED time, midnight or not: under an hour is minutes
    // (German review round, 2026-10-06) — a danger row never says „heute", which undersells a mail
    // that is minutes old.
    const hours = Math.floor(elapsed / HOUR_MS);
    compact = hours < 1 ? `${Math.max(1, Math.floor(elapsed / 60000))} Min.` : `${hours} Std.`;
  } else if (days === 0) {
    compact = 'heute';
  } else if (days < 14) {
    compact = `${days} Tg.`;
  } else if (days < 56) {
    const weeks = Math.floor(days / 7);
    compact = `${weeks} Wo.`;
  } else {
    const months = Math.floor(days / 30);
    compact = `${months} Mon.`;
  }
  return { compact, clock, stampIso };
};

// The arrival's hover/detail text — ONE helper for the list row's hover (ArrivalAge) and the ticket detail's
// Absender line, so the detail prints exactly what the row hovers: „Eingang <stamp>", or — when the customer's
// stamp is missing, in the future or implausible (ageText falls back to our receipt stamp) —
// „Eingang laut Upload <stamp>".
export const arrivalHoverText = (receivedAtIso: string | null | undefined, createdAtIso: string | null | undefined): string => {
  if (!receivedAtIso && !createdAtIso) return ''; // no stamp at all: nothing to state
  const age = ageText(receivedAtIso, createdAtIso);
  return age.clock === 'upload'
    ? `Eingang laut Upload ${receivedStampText(age.stampIso)}`
    : `Eingang ${receivedStampText(age.stampIso)}`;
};

// Shared row-date formatter — DD.MM.YY (de-DE). Used by DecisionBadges' detail variant and by
// callers that render a date themselves (Archiv's compact line 1, the Einstellungen preview).
export const formatRowDate = (iso?: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '';

// THE ARRIVAL STAMP minus its word — „DD.MM.YY HH:MM" alone. It is the stamp inside the row-1
// age's hover (ArrivalAge: arrivalHoverText prepends „Eingang ", the upload clock
// „Eingang laut Upload ") — part 7 fix loop, 2026-10-12: the arrival has one clock, so this stamp
// is hovered there and stated nowhere else on a list row.
// Date and time are composed rather than taken from one toLocaleString call, whose de-DE form
// inserts a comma („13.09.26, 12:00") neither reading wants.
export const receivedStampText = (iso?: string | null): string => {
  if (!iso) return '';
  const time = new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  return `${formatRowDate(iso)} ${time}`;
};

/** Display text for a secondary-axis pill: de-slugged and lowercased.
 *  Lowercasing is EXPLICIT rather than inherited from the corpus — every id ships lowercase today,
 *  so relying on that would make the rule and the coincidence indistinguishable, and a future
 *  mixed-case id would silently break the ruling with no test to catch it. */
export const formatSecondaryAxisCode = (code: string): string => code.replace(/_/g, ' ').toLowerCase();

/** A percentage as the descriptor pills print it: „86 %" — whole number, a space before the sign. */
export const formatPercentFace = (fraction: number): string => `${Math.round(fraction * 100)} %`;

/** A duration in milliseconds as the descriptor pills print it: „10,7 s" — de-DE, one decimal. */
export const formatSecondsFace = (ms: number): string => `${formatDeOneDecimal(ms / 1000)} s`;
